/**
 * Right Drawer Store
 *
 * This store provides outer functionality and computed getters for the right drawer component.
 * The internal state cannot be modified directly from outside - state changes can only be
 * performed by calling the provided functions. This ensures controlled state management
 * and prevents unauthorized state mutations.
 *
 * Features:
 * - Computed properties for reactive state checking
 * - Controlled state modification through dedicated functions
 * - Module-based drawer management system
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";

export const useRightDrawerStore = defineStore("rightDrawer", () => {
    /**
     * Available drawer modules
     * @readonly
     */
    const MODULES = {
        NONE: "",
        ADMINISTRATION: "administration",
        WORKDAY: "workday",
        INVITER_ADMIN: "inviterAdmin",
        OFFICE_HOURS: "officeHours",
        REPORTS: "reports",
        ALIGN: "align",
        TIMEZONES: "timezones",
        DIVISION: "division",
        MEMBER_SELECT: "memberSelect",
        WORKER_SETTINGS: "workerSettings",
    };

    /**
     * Internal drawer state - cannot be modified directly from outside
     * @private
     */
    const drawerState = ref({
        open: false,
        module: MODULES.NONE,
    });

    /**
     * Computed property to check if any drawer module is currently active
     * @returns {boolean} True if drawer is open and has an active module
     */
    const isActiveDrawer = computed(
        () =>
            drawerState.value.open && drawerState.value.module !== MODULES.NONE,
    );

    /**
     * Computed property to check if administration module is currently active
     * @returns {boolean} True if administration module is active
     */
    const isAdministrationActive = computed(() =>
        isModuleActive(MODULES.ADMINISTRATION),
    );

    /**
     * Computed property to check if workday module is currently active
     * @returns {boolean} True if workday module is active
     */
    const isWorkdayActive = computed(() => isModuleActive(MODULES.WORKDAY));

    /**
     * Computed property to check if inviterAdmin module is currently active
     * @returns {boolean} True if inviterAdmin module is active
     */
    const isInviterAdminActive = computed(() =>
        isModuleActive(MODULES.INVITER_ADMIN),
    );

    /**
     * Computed property to check if office hours module is currently active
     * @returns {boolean} True if office hours module is active
     */
    const isOfficeHoursActive = computed(() =>
        isModuleActive(MODULES.OFFICE_HOURS),
    );

    /**
     * Computed property to check if reports module is currently active
     * @returns {boolean} True if reports module is active
     */
    const isReportsActive = computed(() => isModuleActive(MODULES.REPORTS));

    /**
     * Computed property to check if align module is currently active
     * @returns {boolean} True if align module is active
     */
    const isAlignActive = computed(() => isModuleActive(MODULES.ALIGN));

    /**
     * Computed property to check if timezones module is currently active
     * @returns {boolean} True if timezones module is active
     */
    const isTimezonesActive = computed(() => isModuleActive(MODULES.TIMEZONES));

    /**
     * Computed property to check if division module is currently active
     * @returns {boolean} True if division module is active
     */
    const isDivisionActive = computed(() => isModuleActive(MODULES.DIVISION));
    
    /**
     * Computed property to check if member select module is currently active
     * @returns {boolean} True if member select module is active
     */
    const isMemberSelectActive = computed(() =>
        isModuleActive(MODULES.MEMBER_SELECT),
    );

    /**
     * Computed property to check if worker settings module is currently active
     * @returns {boolean} True if worker settings module is active
     */
    const isWorkerSettingsActive = computed(() =>
        isModuleActive(MODULES.WORKER_SETTINGS),
    );

    /**
     * Check if a specific module is currently active
     * @param {string} module - The module to check
     * @returns {boolean} True if the specified module is active and drawer is open
     */
    function isModuleActive(module) {
        return drawerState.value.module === module && drawerState.value.open;
    }

    /**
     * Open the drawer with a specific module
     * This is the primary way to activate the drawer with a module from outside
     * @param {string} module - The module to activate (should be one of MODULES values)
     */
    function openDrawer(module) {
        drawerState.value.open = true;
        drawerState.value.module = module;
    }

    /**
     * Close the drawer and reset module to NONE
     * This is the only way to close the drawer from outside
     */
    function closeDrawer() {
        drawerState.value.open = false;
        drawerState.value.module = MODULES.NONE;
    }

    /**
     * Open the administration module
     * This opens the drawer and activates the administration module
     */
    function openAdministration() {
        openDrawer(MODULES.ADMINISTRATION);
    }

    /**
     * Open the workday module
     * This opens the drawer and activates the workday module
     */
    function openWorkday() {
        openDrawer(MODULES.WORKDAY);
    }

    /**
     * Open the inviterAdmin module
     * This opens the drawer and activates the inviterAdmin module
     */
    function openInviterAdmin() {
        openDrawer(MODULES.INVITER_ADMIN);
    }

    /**
     * Open the office hours module
     * This opens the drawer and activates the office hours module
     */
    function openOfficeHours() {
        openDrawer(MODULES.OFFICE_HOURS);
    }

    /**
     * Open the reports module
     * This opens the drawer and activates the reports module
     */
    function openReports() {
        openDrawer(MODULES.REPORTS);
    }

    /**
     * Open the align module
     * This opens the drawer and activates the align module
     */
    function openAlign() {
        openDrawer(MODULES.ALIGN);
    }

    /**
     * Open the timezones module
     * This opens the drawer and activates the timezones module
     */
    function openTimezones() {
        openDrawer(MODULES.TIMEZONES);
    }

    /**
     * Open the division module
     * This opens the drawer and activates the division module
     */
    function openDivision() {
        openDrawer(MODULES.DIVISION);
    }
  
    /**
     * Open the member select drawer module
     */
    function openMemberSelect() {
        openDrawer(MODULES.MEMBER_SELECT);
    }

    /**
     * Open the worker settings module
     */
    function openWorkerSettings() {
        openDrawer(MODULES.WORKER_SETTINGS);
    }

    // Expose only the necessary functions and computed properties
    // Internal state remains protected from direct external modification
    return {
        // Functions for controlled state modification
        openAdministration,
        openWorkday,
        openInviterAdmin,
        openOfficeHours,
        openReports,
        openAlign,
        openTimezones,
        openDivision,
        openMemberSelect,
        openWorkerSettings,
        closeDrawer,

        // Computed getters for reactive state checking
        isAdministrationActive,
        isActiveDrawer,
        isWorkdayActive,
        isInviterAdminActive,
        isOfficeHoursActive,
        isReportsActive,
        isAlignActive,
        isTimezonesActive,
        isDivisionActive,
        isMemberSelectActive,
        isWorkerSettingsActive,
    };
});
